import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// CLE-DASH — configurable task statuses. Mirrors backend
// src/routes/taskStatuses.ts serializeStatus.

export type TaskStatusCategory = 'BACKLOG' | 'UNSTARTED' | 'STARTED' | 'COMPLETED' | 'CANCELLED';

export interface TaskStatusDef {
  id: string;
  name: string;
  /** Stable machine key ("in_progress") — survives renames, used in URLs. */
  slug: string;
  color: string;
  order: number;
  category: TaskStatusCategory;
  isDefault: boolean;
  taskCount: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listTaskStatuses(): Promise<TaskStatusDef[]> {
  const res = await apiFetch<Envelope<TaskStatusDef[]>>('/api/task-statuses', {
    headers: authHeaders(),
  });
  return res.data;
}

export interface CreateTaskStatusInput {
  name: string;
  color?: string;
  category?: TaskStatusCategory;
  order?: number;
}

export async function createTaskStatus(input: CreateTaskStatusInput): Promise<TaskStatusDef> {
  const res = await apiFetch<Envelope<TaskStatusDef>>('/api/task-statuses', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export interface UpdateTaskStatusInput {
  name?: string;
  color?: string;
  category?: TaskStatusCategory;
  isDefault?: boolean;
}

export async function updateTaskStatus(
  id: string,
  input: UpdateTaskStatusInput,
): Promise<TaskStatusDef> {
  const res = await apiFetch<Envelope<TaskStatusDef>>(`/api/task-statuses/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function reorderTaskStatuses(statusIds: string[]): Promise<TaskStatusDef[]> {
  const res = await apiFetch<Envelope<TaskStatusDef[]>>('/api/task-statuses/reorder', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ statusIds }),
  });
  return res.data;
}

export async function deleteTaskStatus(
  id: string,
  reassignTo?: string,
): Promise<{ deleted: boolean; id: string; reassignedTasks: number }> {
  const qs = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : '';
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string; reassignedTasks: number }>>(
    `/api/task-statuses/${id}${qs}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}
